import { Role } from '@epilot/permissions/types'
import { Row } from '@tanstack/table-core'
import { debounce } from 'lodash'
import { LogInIcon, MousePointerClick } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LoginAsUserDialog } from '@/components/login-as-user-dialog'
import { SheetJSONEditor } from '@/components/sheet-json-editor'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/ui/data-table'
import { DataTableRowActions } from '@/components/ui/data-table-row-actions'
import { useJSONEditor } from '@/hooks/useJSONEditor'
import { useUserTable } from '@/hooks/useUserTable'
import { IUser } from '@/models'
import { useAuth } from '@/providers'
import { useOrganizationCleanup } from '@/providers/OrganizationCleanupContextProvider'
import { RoutePaths } from '@/routes/routes'
import { useQueryGetUsersActivity } from '@/services'
import { Permissions, isPermitted } from '@/utils/permission-manager'
import { getUserActivityKey } from '@/utils/user-activity'

type UserListProps = {
  organizationId?: string
  loginAsDisabled?: boolean
  loginAsUserDialogProps?: Omit<
    React.ComponentProps<typeof LoginAsUserDialog>,
    'onOpenChange' | 'open' | 'user'
  >
  tableProps?: Omit<
    React.ComponentProps<typeof DataTable<IUser, IUser>>,
    'data' | 'tableConfigName' | 'columns'
  >
}

const UserList = ({
  organizationId,
  tableProps,
  loginAsUserDialogProps,
  loginAsDisabled = false
}: UserListProps) => {
  const [search, _setSearch] = useState<string>()

  const setSearch = debounce((search) => {
    _setSearch(search)
  }, 300)

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  })
  const [loadAll, setLoadAll] = useState(false)

  const {
    columns,
    data: users,
    totalCount,
    isLoading,
    isReadyExport,
    isRolesLoading
  } = useUserTable({
    orgId: organizationId,
    limit: pagination.pageSize,
    page: pagination.pageIndex + 1,
    fetchAll: loadAll,
    search
  })

  // one batched activity lookup (chunked by 200 ids) for the rows currently loaded
  const { data: activityByUser, isLoading: isActivityLoading } =
    useQueryGetUsersActivity(
      users.map((user) => user.id),
      organizationId
    )

  const data = useMemo<IUser[]>(
    () =>
      activityByUser
        ? users.map((user) => ({
            ...user,
            activity:
              activityByUser[getUserActivityKey(user.organization_id, user.id)]
          }))
        : users,
    [users, activityByUser]
  )

  const { organizationsToCleanup, isLoading: isLoadingOrganizationsToCleanup } =
    useOrganizationCleanup()
  const { userData } = useAuth()
  const loginAsPermitted = isPermitted(Permissions.LOGIN_AS, userData)
  const navigate = useNavigate()
  const jsonEditorProps = useJSONEditor()
  const [loginAsDialogOpen, setLoginAsDialogOpen] = useState(false)
  const [loginAsUser, setLoginAsUser] = useState<
    | {
        email: string
        orgId: string
      }
    | undefined
  >()

  const shouldShowLoginAsButton = (org_id: string) =>
    loginAsPermitted &&
    !loginAsDisabled &&
    !isLoadingOrganizationsToCleanup &&
    !organizationsToCleanup?.some((org) => org.org_id === org_id)

  // useEffect(() => {
  //   if (loadAll) {
  //     setPagination({ pageIndex: 0, pageSize: pagination.pageSize })
  //   }
  // }, [loadAll, pagination.pageSize])

  return (
    <>
      <DataTable
        columns={columns.map((column) => {
          if (column.id === 'roles') {
            return {
              ...column,
              cell: isRolesLoading
                ? ({ row: _row }: { row: Row<IUser> }) => (
                    <div className="h-4 bg-muted rounded-lg animate-pulse" />
                  )
                : ({ row }: { row: Row<IUser> }) => {
                    const roles = data[row.index].roles ?? []

                    return (
                      <div className="flex overflow-y-scroll h-full py-3">
                        {(roles as Role[])?.map((role) => (
                          <Badge
                            className="bg-secondary hover:bg-hover cursor-pointer mr-2 min-w-min whitespace-nowrap"
                            key={role?.id + row.index}
                            onClick={(e) => {
                              e.stopPropagation()
                              jsonEditorProps.setOriginalData(role)
                              jsonEditorProps.setOpen(true)
                            }}
                            variant={'outline'}
                          >
                            {role?.name}{' '}
                            <MousePointerClick className="w-4 h-4 ml-1" />
                          </Badge>
                        ))}
                      </div>
                    )
                  }
            }
          } else if (
            isActivityLoading &&
            users.length > 0 &&
            (column.id === 'last_login_at' || column.id === 'last_seen_at')
          ) {
            return {
              ...column,
              cell: () => (
                <div className="h-4 bg-muted rounded-lg animate-pulse" />
              )
            }
          } else if (column.id === 'actions') {
            return {
              ...column,
              cell: ({ row }: { row: Row<IUser> }) => (
                <DataTableRowActions
                  items={[
                    ...(shouldShowLoginAsButton(row.original.organization_id!)
                      ? [
                          {
                            icon: <LogInIcon className="w-4 h-4 mr-2" />,
                            label: 'Login as',
                            disabled: row.original.status !== 'Active',
                            onClick: async () => {
                              const email = row.original.email

                              if (email) {
                                setLoginAsUser({
                                  email,
                                  orgId: row.original.organization_id!
                                })
                                setLoginAsDialogOpen(true)
                              }
                            }
                          }
                        ]
                      : [])
                  ]}
                  key={column.id}
                  row={row}
                />
              )
            }
          }

          return column
        })}
        data={data}
        defaultGlobalFilter={search}
        defaultSorting={[{ id: 'created_at', desc: false }]}
        forceSelectAll={
          loadAll && totalCount !== undefined && data.length === totalCount
        }
        getRowClassName={(row) =>
          row.original.email?.includes('@testuser.epilot.cloud')
            ? 'bg-yellow-500/20'
            : undefined
        }
        isLoading={isLoading}
        manualPagination={
          loadAll
            ? undefined
            : {
                pagination,
                setPagination,
                rowCount: totalCount || 0
              }
        }
        onRowClick={(row) => {
          navigate(
            `${RoutePaths.USERS}/${row.original.id}?orgId=${row.original.organization_id}`
          )
        }}
        onRowSelectionChange={(sel) => {
          const selected = Object.values(sel).filter(Boolean).length

          if (
            !loadAll &&
            data.length > 0 &&
            selected > 0 &&
            selected === data.length
          ) {
            setLoadAll(true)
          }
        }}
        setDefaultGlobalFilter={setSearch}
        tableConfigName="user"
        toolbarProps={{
          exportButtonProps: {
            loading: !isReadyExport
          }
        }}
        {...tableProps}
      />
      <SheetJSONEditor {...jsonEditorProps} />
      <LoginAsUserDialog
        onOpenChange={(open) => setLoginAsDialogOpen(open)}
        open={loginAsDialogOpen}
        user={loginAsUser!}
        {...loginAsUserDialogProps}
      />
    </>
  )
}

export default UserList
